import React, { useEffect, useState } from 'react';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../lib/designSystem/Table';
import {
  getAllNotRecognizedPayments,
  getNotRecognizedPayment,
} from '../../../../../../lib/api';
import { useDispatch, useSelector } from 'react-redux';
import CustomAlert from '../../../../../../lib/designSystem/Alert';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import NotRecognizedPaymentDetails from './components/NotRecognizedPaymentDetails';
import { Autocomplete, TextField } from '@mui/material';
import { validatePermissions } from '../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../services/redux/authSlice';
import DocumentSearcher from '../../../../../../lib/common/DocumentSearcher';
import TableWithPagination from '../../../../../../lib/designSystem/TableWithPagination';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'id', label: 'ID', type: 'numeric-id' },
  { key: 'stakeholderName', label: 'Origen' },
  { key: 'stakeholderIdentifier', label: 'RUT' },
  { key: 'createdAt', label: 'Fecha', type: 'date' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'status', label: 'Estado', type: 'conciliation-status' },
];

const FNC: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  // get invoices from /api/internal/collection/invoices/:company_id
  const [notRecognizedPayments, setNotRecognizedPayments] = useState<any[]>([]);
  const [notRecognizedPayment, setNotRecognizedPayment] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState('');
  const [failure, setFailure] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [status, setStatus] = useState('PENDING');
  const statusOptions = [
    { label: 'Pendiente', value: 'PENDING' },
    { label: 'Conciliado', value: 'CONCILIATED' },
  ];
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (status) query += `status=${status}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchFNC = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      const query = generateQuery({ page, pageSize });
      const { data, pagination } = await getAllNotRecognizedPayments(
        dispatch,
        query,
      );
      setNotRecognizedPayments(data);
      setPagination(pagination);
    } catch (error) {
      alert('Error al cargar los FNCS');
    }
  };

  const setUp = async () => {
    setLoading(true);
    await fetchFNC({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  useEffect(() => {
    setUp();
  }, []);

  const handleDetails = async (notRecognizedPaymentId: any, setUp?: any) => {
    setLoading(true);
    try {
      if (setUp) await fetchFNC({ page: 0, pageSize: PAGE_SIZE });
      const response = await getNotRecognizedPayment(
        dispatch,
        notRecognizedPaymentId,
      );
      setNotRecognizedPayment(response);
      setOpen(true);
    } catch (error) {
      console.log(error);
      alert('Error al cargar los FNCs');
    }
    setLoading(false);
  };

  const canShowFNCAssign = () => {
    if (validatePermissions(user, 'financing_fnc_assign')) return true;

    return false;
  };

  const actions: any = [
    {
      action: handleDetails,
      actionLabel: 'Detalles',
      canShow: canShowFNCAssign,
    },
    // {action: handleDelete, actionLabel: 'Borrar', canShow: canShowDeleteFS},
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        // buttons={buttons}
        variant="body1"
        title="Acá podrás ver los los pagos no reconocidos generados."
        buttons={[
          {
            name: 'Filtro',
            action: () => {},
            color: 'primary',
            customButton: (
              <DocumentSearcher
                searchData={fetchFNC}
                setData={setNotRecognizedPayments}
                setLoading={setLoading}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                stakeholderIdentifier={stakeholderIdentifier}
                setStakeholderIdentifier={setStakeholderIdentifier}
                pagination={pagination}
                status={status}
                setStatus={setStatus}
                statusOptions={statusOptions}
              />
            ),
          },
        ]}
      />
      <TableWithPagination
        dataHeaders={headers}
        searchData={fetchFNC}
        data={notRecognizedPayments}
        rowsPerPageDefault={50}
        actions={actions}
        exportToFile="xlsx"
        lastElement
        setData={setNotRecognizedPayments}
        pagination={pagination}
        setPagination={setPagination}
        loading={loading}
        setLoading={setLoading}
      />
      {open && (
        <NotRecognizedPaymentDetails
          open={open}
          setOpen={setOpen}
          notRecognizedPayment={notRecognizedPayment}
          setNotRecognizedPayment={setNotRecognizedPayment}
          setUp={fetchFNC}
          setSuccessOpen={setSuccess}
          setFailureOpen={setFailure}
          handleDetails={handleDetails}
        />
      )}
      <CustomAlert
        open={success.length}
        title="Éxito"
        onClose={() => {
          setSuccess('');
        }}
        setOpen={setSuccess}
        text={success}
        severity="success"
        size="small"
        variant="outlined"
      />
      <CustomAlert
        open={failure.length}
        title="Error"
        onClose={() => {
          setFailure('');
        }}
        setOpen={setFailure}
        text={failure}
        severity="error"
        size="small"
        variant="outlined"
      />
    </div>
  );
};

export default FNC;
